import type { EmailAgentConfig } from "@/lib/agents/email/types"

export type EmailReplyCategorySettings = NonNullable<
  NonNullable<EmailAgentConfig["auto_reply"]>["category_settings"]
>

export function hasNonSendingEmailCategories(
  categories: readonly string[],
  settings: EmailReplyCategorySettings,
): boolean {
  return categories.some((category) => {
    const categorySettings = settings[category]
    return !categorySettings?.enabled
      || (categorySettings.dry_run ?? true)
      || (categorySettings.create_draft_instead_of_send ?? false)
  })
}

export function buildSendAllEmailCategorySettings(
  categories: readonly string[],
  currentSettings: EmailReplyCategorySettings,
  selectedCategories: readonly string[] = [],
): Pick<NonNullable<EmailAgentConfig["auto_reply"]>, "category_settings" | "selected_categories"> {
  const uniqueAvailableCategories = uniqueCategories(categories)
  const categorySettings = { ...currentSettings }

  for (const category of uniqueAvailableCategories) {
    categorySettings[category] = {
      ...currentSettings[category],
      enabled: true,
      dry_run: false,
      create_draft_instead_of_send: false,
    }
  }

  return {
    category_settings: categorySettings,
    selected_categories: uniqueCategories([...selectedCategories, ...uniqueAvailableCategories]),
  }
}

/**
 * Write the two category keys the email agent needs, together and consistently.
 *
 * The UI renders each category switch from `auto_reply.category_settings[cat].enabled`
 * while the runtime selects mail with `auto_reply.selected_categories` — writing only one
 * produces an agent that answers everything while the screen shows every category off.
 *
 * Without `enableAutoReply` the result is the "Drafts" shape (`dry_run: false`,
 * `create_draft_instead_of_send: true`) and neither `enabled` nor `start_date` is touched:
 * that is what agent creation wants, since `start_date` belongs to the moment an agent
 * actually goes live. With it, the agent is switched on and dated — the onboarding
 * activation step.
 */
export function buildEmailAutoReplyCategoryConfig({
  config,
  availableCategories,
  selectedCategories,
  enableAutoReply,
}: {
  config: EmailAgentConfig
  availableCategories: readonly string[]
  selectedCategories?: readonly string[]
  enableAutoReply?: boolean
}): EmailAgentConfig {
  const available = uniqueCategories(availableCategories)
  const availableSet = new Set(available)
  const currentAutoReply = config.auto_reply ?? {}
  const currentSettings = currentAutoReply.category_settings ?? {}
  const hasExistingCategoryConfiguration = available.some(
    (category) => currentSettings[category] !== undefined,
  )
  const selected = uniqueCategories(
    selectedCategories ??
      (hasExistingCategoryConfiguration
        ? (currentAutoReply.selected_categories ?? [])
        : available),
  ).filter((category) => availableSet.has(category))
  const selectedSet = new Set(selected)

  const categorySettings = Object.fromEntries(
    available.map((category) => {
      const current = currentSettings[category]
      return [
        category,
        {
          ...current,
          enabled: selectedSet.has(category),
          dry_run: enableAutoReply ? false : (current?.dry_run ?? false),
          create_draft_instead_of_send:
            enableAutoReply
              ? true
              : (current?.create_draft_instead_of_send ?? true),
        },
      ]
    }),
  ) satisfies EmailReplyCategorySettings

  return {
    ...config,
    auto_reply: {
      ...currentAutoReply,
      dry_run: enableAutoReply ? false : (currentAutoReply.dry_run ?? false),
      create_draft_instead_of_send:
        enableAutoReply
          ? true
          : (currentAutoReply.create_draft_instead_of_send ?? true),
      ...(enableAutoReply
        ? {
            enabled: true,
            start_date: new Date().toISOString().split("T")[0],
          }
        : {}),
      selected_categories: selected,
      category_settings: categorySettings,
    },
  }
}

function uniqueCategories(categories: readonly string[]): string[] {
  return [
    ...new Set(categories.map((category) => category.trim()).filter(Boolean)),
  ]
}
